/**
 * Web-compatible version of CustomSelectionDot component.
 * This replaces the Skia-based SelectionDot for web builds.
 *
 * Usage:
 *
 * ```jsx
 * <LineGraph
 *   points={priceHistory}
 *   animated={true}
 *   enablePanGesture={true}
 *   SelectionDot={CustomSelectionDot}
 * />
 * ```
 *
 * This example has removed the outer ring and light
 * shadow from the default one to make it more flat.
 */
import React, { useCallback, useEffect, useState } from 'react'
import {
  runOnJS,
  useAnimatedReaction,
  withSpring,
  useSharedValue,
} from 'react-native-reanimated'
import { View } from 'react-native'
import type { SelectionDotProps } from 'react-native-graph'

export function SelectionDot({
  isActive,
  color,
  circleX,
  circleY,
}: SelectionDotProps): React.ReactElement {
  const circleRadius = useSharedValue(0)
  const [animatedRadius, setAnimatedRadius] = useState(0)

  const setIsActive = useCallback(
    (active: boolean) => {
      const targetRadius = active ? 5 : 0
      circleRadius.value = withSpring(targetRadius, {
        mass: 1,
        stiffness: 1000,
        damping: 50,
        velocity: 0,
      })
      
      // For web, we need to manually update the radius since we can't use Skia
      setAnimatedRadius(targetRadius)
    },
    [circleRadius]
  )

  useAnimatedReaction(
    () => isActive.value,
    (active) => {
      runOnJS(setIsActive)(active)
    },
    [isActive, setIsActive]
  )

  // Web-compatible circle using a View with border radius
  return (
    <View
      style={{
        position: 'absolute',
        left: circleX - animatedRadius,
        top: circleY - animatedRadius,
        width: animatedRadius * 2,
        height: animatedRadius * 2,
        borderRadius: animatedRadius,
        backgroundColor: color,
        opacity: animatedRadius > 0 ? 1 : 0,
      }}
    />
  )
}
